import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Trash2, Save, Loader2, GripVertical, Crown, RefreshCw,
  Wrench, Zap, Bot, Users, MessageSquare, ShieldCheck, Sparkles, Globe, Terminal,
  Upload, X, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

/**
 * TeamContentManager — CMS panel for the public `/info/team` page.
 *
 * Reads and writes `site_config.team_page` (backend defaults shipped in
 * /app/backend/defaults.py under `team_page`). All updates go through
 * PATCH /api/config so the change is instantly reflected everywhere the
 * public TeamPage.jsx renders.
 *
 * Not to be confused with the existing `TeamManager` (which manages staff
 * user accounts + upload limits — a totally different resource).
 */

const ICON_OPTIONS = [
  { key: 'wrench',   label: 'Wrench (consultation)', Icon: Wrench },
  { key: 'zap',      label: 'Zap (speed / power)',   Icon: Zap },
  { key: 'bot',      label: 'Bot (AI)',              Icon: Bot },
  { key: 'users',    label: 'Users (community)',    Icon: Users },
  { key: 'message',  label: 'Message (content)',    Icon: MessageSquare },
  { key: 'shield',   label: 'Shield (safety)',      Icon: ShieldCheck },
  { key: 'sparkles', label: 'Sparkles',             Icon: Sparkles },
  { key: 'crown',    label: 'Crown (leadership)',   Icon: Crown },
  { key: 'globe',    label: 'Globe (global)',       Icon: Globe },
  { key: 'terminal', label: 'Terminal',             Icon: Terminal },
];

const COLOR_PRESETS = ['#00ff9d', '#ff2d92', '#4de0ff', '#ffd34d', '#c084fc', '#f472b6', '#ff7a3d', '#25d366', '#a78bfa'];

const newMember = () => ({
  id: `m_${Math.random().toString(36).slice(2, 8)}`,
  callsign: 'ERR0R-XXXX',
  role: 'New Operative',
  tagline: 'short one-line bio',
  color: '#00ff9d',
  icon: 'wrench',
  avatar_url: '',
  active: true,
  sort: 999,
});

const emptyStat = () => ({ label: 'NEW STAT', value: '0', color: '#00ff9d' });

const TeamContentManager = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const full = await api.getConfig();
      setCfg(full.team_page || { enabled: true, owner: { stats: [], roles: [], skills: [] }, members: [] });
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const owner = cfg?.owner || {};
  const members = useMemo(() => (cfg?.members || []).slice().sort((a, b) => (a.sort || 0) - (b.sort || 0)), [cfg]);

  // ---- setters ------------------------------------------------------------
  const patchOwner = (p) => setCfg(c => ({ ...c, owner: { ...(c?.owner || {}), ...p } }));
  const patchOwnerList = (key, idx, value) => {
    setCfg(c => {
      const list = [...(c?.owner?.[key] || [])];
      if (value === null) list.splice(idx, 1);
      else list[idx] = value;
      return { ...c, owner: { ...(c?.owner || {}), [key]: list } };
    });
  };
  const addOwnerList = (key, initial) => setCfg(c => ({ ...c, owner: { ...(c?.owner || {}), [key]: [...(c?.owner?.[key] || []), initial] } }));

  const patchMember = (id, p) => setCfg(c => ({ ...c, members: (c?.members || []).map(m => m.id === id ? { ...m, ...p } : m) }));
  const removeMember = (id) => setCfg(c => ({ ...c, members: (c?.members || []).filter(m => m.id !== id) }));
  const addMember = () => setCfg(c => ({ ...c, members: [...(c?.members || []), { ...newMember(), sort: (c?.members?.length || 0) + 1 }] }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const out = await api.patchConfig({ team_page: cfg });
      setCfg(out.team_page || cfg);
      toast.success('Team page saved · live on /info/team');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading || !cfg) {
    return (
      <div className="p-8 grid place-items-center">
        <Loader2 className="animate-spin text-[var(--eh-green)]" size={22} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="team-content-manager">
      {/* HEADER */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="eh-kicker mb-1">// CMS · TEAM_PAGE</div>
          <h2 className="font-bold text-lg" style={{ fontFamily: 'Inter,sans-serif' }}>Team Page Editor</h2>
          <div className="text-xs opacity-70 mt-1" style={{ fontFamily: 'Inter,sans-serif' }}>
            Edit the founder card + crew grid on <code className="eh-mono text-[11px]">/info/team</code>. Changes go live instantly.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs eh-mono opacity-80">
            <input type="checkbox" checked={cfg.enabled !== false} onChange={e => setCfg(c => ({ ...c, enabled: e.target.checked }))} data-testid="team-enabled" />
            PAGE ENABLED
          </label>
          <button onClick={load} className="eh-btn-ghost text-xs" title="Reload from server"><RefreshCw size={12} /> RELOAD</button>
          <button onClick={save} disabled={saving} className="eh-btn-primary text-xs disabled:opacity-50" data-testid="team-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} SAVE
          </button>
        </div>
      </div>

      {/* ==================== OWNER SECTION ==================== */}
      <section className="eh-panel p-5 sm:p-6 space-y-4" data-testid="team-owner-editor">
        <div className="flex items-center gap-2 pb-2 border-b border-[var(--eh-border)]">
          <Crown size={16} className="text-[#ffd34d]" />
          <h3 className="font-bold text-base" style={{ fontFamily: 'Inter,sans-serif' }}>Founder Card</h3>
        </div>

        <div className="grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-6 items-start">
          {/* Avatar uploader — royal frame preview + upload */}
          <AvatarUploader
            value={owner.avatar_url}
            onChange={(url) => patchOwner({ avatar_url: url })}
            fallbackText={(owner.callsign || 'X').replace(/[^A-Z0-9]/gi, '').slice(0, 2).toUpperCase()}
            accent="#ffd34d"
            size="lg"
            testid="owner-avatar-uploader"
          />
          <div className="grid sm:grid-cols-2 gap-3 min-w-0">
            <Field label="Callsign (ERR0R-CIPHER)" value={owner.callsign} onChange={v => patchOwner({ callsign: v })} testid="owner-callsign" />
            <Field label="Display name" value={owner.name} onChange={v => patchOwner({ name: v })} testid="owner-name" />
            <Field label="Legal hint (small text under name)" value={owner.legal_hint} onChange={v => patchOwner({ legal_hint: v })} testid="owner-legal" />
            <Field label="Tagline (one line under stats)" value={owner.tagline} onChange={v => patchOwner({ tagline: v })} testid="owner-tagline" />
            <Field label="Telegram URL (leave blank to use site default)" value={owner.telegram_url} onChange={v => patchOwner({ telegram_url: v })} testid="owner-telegram" />
            <Field label="Email (leave blank to use site default)" value={owner.email} onChange={v => patchOwner({ email: v })} testid="owner-email" />
            <TextArea label="Signature quote" value={owner.quote} onChange={v => patchOwner({ quote: v })} rows={2} testid="owner-quote" cols={2} />
          </div>
        </div>

        {/* Roles */}
        <ListEditor
          title="Rotating job titles (cycle every 2.6s on the site)"
          items={owner.roles || []}
          onChange={(list) => patchOwner({ roles: list })}
          placeholder="e.g. Ethical Hacker · OSCP"
          testid="owner-roles"
        />

        {/* Stats */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="eh-mono text-[10px] tracking-widest opacity-70">// STATS · 4 CARDS RECOMMENDED</div>
            <button onClick={() => addOwnerList('stats', emptyStat())} className="eh-btn-ghost text-xs"><Plus size={12} /> ADD</button>
          </div>
          <div className="space-y-2">
            {(owner.stats || []).map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`owner-stat-${i}`}>
                <input value={s.label || ''} onChange={e => patchOwnerList('stats', i, { ...s, label: e.target.value })} placeholder="LABEL" className="eh-input col-span-5 text-xs" />
                <input value={s.value || ''} onChange={e => patchOwnerList('stats', i, { ...s, value: e.target.value })} placeholder="VALUE" className="eh-input col-span-3 text-xs" />
                <ColorSwatch value={s.color} onChange={v => patchOwnerList('stats', i, { ...s, color: v })} className="col-span-3" />
                <button onClick={() => patchOwnerList('stats', i, null)} className="eh-btn-ghost col-span-1 text-red-400"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Skills */}
        <ListEditor
          title="Skills (marquee ticker at bottom)"
          items={owner.skills || []}
          onChange={(list) => patchOwner({ skills: list })}
          placeholder="e.g. account recovery"
          testid="owner-skills"
        />
      </section>

      {/* ==================== CREW SECTION ==================== */}
      <section className="eh-panel p-5 sm:p-6 space-y-3" data-testid="team-crew-editor">
        <div className="flex items-center justify-between pb-2 border-b border-[var(--eh-border)]">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-[var(--eh-green)]" />
            <h3 className="font-bold text-base" style={{ fontFamily: 'Inter,sans-serif' }}>Crew Grid ({members.length})</h3>
          </div>
          <button onClick={addMember} className="eh-btn-primary text-xs" data-testid="crew-add"><Plus size={12} /> ADD OPERATIVE</button>
        </div>

        {members.length === 0 ? (
          <div className="text-center text-sm opacity-60 py-6">No crew yet. Click ADD OPERATIVE to seed one.</div>
        ) : (
          <div className="space-y-3">
            {members.map(m => (
              <div key={m.id} className="rounded border p-3 space-y-2" style={{ borderColor: `${m.color}55`, background: `linear-gradient(135deg, ${m.color}08, transparent 60%)` }} data-testid={`crew-row-${m.id}`}>
                <div className="flex items-center gap-2">
                  <GripVertical size={14} className="opacity-40 shrink-0" />
                  <input type="number" value={m.sort || 0} onChange={e => patchMember(m.id, { sort: Number(e.target.value) })} className="eh-input w-16 text-xs" title="Sort order" />
                  <input value={m.callsign || ''} onChange={e => patchMember(m.id, { callsign: e.target.value })} placeholder="ERR0R-XXXX" className="eh-input text-xs flex-1 min-w-0 font-mono" style={{ color: m.color }} />
                  <label className="flex items-center gap-1 text-[10px] eh-mono opacity-80 shrink-0">
                    <input type="checkbox" checked={m.active !== false} onChange={e => patchMember(m.id, { active: e.target.checked })} /> ACTIVE
                  </label>
                  <button onClick={() => removeMember(m.id)} className="eh-btn-ghost text-red-400 shrink-0" data-testid={`crew-remove-${m.id}`}><Trash2 size={12} /></button>
                </div>
                <div className="grid sm:grid-cols-[auto_1fr] gap-3 sm:gap-4 items-start">
                  {/* Per-member avatar uploader */}
                  <AvatarUploader
                    value={m.avatar_url}
                    onChange={(url) => patchMember(m.id, { avatar_url: url })}
                    fallbackText={(m.callsign || 'X').replace(/[^A-Z0-9]/gi, '').slice(0, 2).toUpperCase()}
                    accent={m.color}
                    size="sm"
                    testid={`crew-avatar-${m.id}`}
                  />
                  <div className="grid sm:grid-cols-2 gap-2 min-w-0">
                    <input value={m.role || ''} onChange={e => patchMember(m.id, { role: e.target.value })} placeholder="Role · e.g. Recovery Lead" className="eh-input text-xs" />
                    <input value={m.tagline || ''} onChange={e => patchMember(m.id, { tagline: e.target.value })} placeholder="Tagline" className="eh-input text-xs" />
                    <select value={m.icon || 'wrench'} onChange={e => patchMember(m.id, { icon: e.target.value })} className="eh-input text-xs" title="Fallback icon (used when no photo uploaded)">
                      {ICON_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                    <ColorSwatch value={m.color} onChange={v => patchMember(m.id, { color: v })} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="eh-btn-primary disabled:opacity-50" data-testid="team-save-bottom">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} SAVE ALL
        </button>
      </div>
    </div>
  );
};

// ---------- Reusable field editors ----------

const Field = ({ label, value, onChange, testid, cols = 1 }) => (
  <div className={cols === 2 ? 'sm:col-span-2' : ''}>
    <label className="eh-mono text-[10px] tracking-widest opacity-60 mb-1 block">{label.toUpperCase()}</label>
    <input value={value || ''} onChange={e => onChange(e.target.value)} className="eh-input w-full text-sm" data-testid={testid} />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 3, testid, cols = 1 }) => (
  <div className={cols === 2 ? 'sm:col-span-2' : ''}>
    <label className="eh-mono text-[10px] tracking-widest opacity-60 mb-1 block">{label.toUpperCase()}</label>
    <textarea rows={rows} value={value || ''} onChange={e => onChange(e.target.value)} className="eh-input w-full text-sm resize-y" data-testid={testid} />
  </div>
);

const ColorSwatch = ({ value, onChange, className = '' }) => (
  <div className={`flex items-center gap-1 ${className}`}>
    <input type="color" value={value || '#00ff9d'} onChange={e => onChange(e.target.value)} className="h-7 w-8 rounded border border-[var(--eh-border)] bg-transparent" title={value} />
    <input value={value || ''} onChange={e => onChange(e.target.value)} className="eh-input text-xs flex-1 min-w-0" placeholder="#00ff9d" />
    <div className="hidden sm:flex gap-1">
      {COLOR_PRESETS.slice(0, 4).map(c => (
        <button key={c} type="button" onClick={() => onChange(c)} className="w-4 h-4 rounded-full border border-[var(--eh-border)]" style={{ background: c }} title={c} />
      ))}
    </div>
  </div>
);

const ListEditor = ({ title, items, onChange, placeholder, testid }) => {
  const update = (i, val) => {
    const list = [...items];
    if (val === null) list.splice(i, 1); else list[i] = val;
    onChange(list);
  };
  const add = () => onChange([...items, '']);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="eh-mono text-[10px] tracking-widest opacity-70">// {title.toUpperCase()}</div>
        <button onClick={add} className="eh-btn-ghost text-xs" data-testid={`${testid}-add`}><Plus size={12} /> ADD</button>
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`${testid}-row-${i}`}>
            <input value={it || ''} onChange={e => update(i, e.target.value)} placeholder={placeholder} className="eh-input text-xs flex-1" />
            <button onClick={() => update(i, null)} className="eh-btn-ghost text-red-400"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- AvatarUploader ----------
// Reusable admin control for uploading a member DP.
// Shows a live preview, delegates upload to POST /api/uploads via api.uploadImage,
// and sets `value` to the returned absolute URL. Falls back to a monogram tile
// when no photo is set.
const AvatarUploader = ({ value, onChange, fallbackText = 'XX', accent = '#00ff9d', size = 'sm', testid = 'avatar-uploader' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const dim = size === 'lg' ? 'w-24 h-24' : 'w-16 h-16';
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const previewSrc = value
    ? (value.startsWith('http') || value.startsWith('data:') ? value : `${backendUrl}${value}`)
    : '';

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5MB — please pick a smaller image');
      return;
    }
    setUploading(true);
    try {
      const out = await api.uploadImage(file);
      // Store as absolute path so the public TeamPage can render across envs
      onChange(out.url);
      toast.success('Photo uploaded · click SAVE to publish');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col items-center gap-1.5" data-testid={testid}>
      <div
        className={`relative ${dim} rounded-full grid place-items-center overflow-hidden shrink-0`}
        style={{
          background: previewSrc ? 'transparent' : `radial-gradient(circle at 30% 30%, ${accent}25, transparent 65%), #0d1115`,
          boxShadow: `0 0 0 1px ${accent}55, 0 0 22px -8px ${accent}`,
          border: `1.5px solid ${accent}66`,
        }}
      >
        {previewSrc ? (
          <img src={previewSrc} alt="dp" className="w-full h-full object-cover" data-testid={`${testid}-img`} />
        ) : (
          <div className="eh-mono font-bold text-sm sm:text-base" style={{ color: accent }} data-testid={`${testid}-monogram`}>
            {fallbackText}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[10px] eh-mono tracking-widest disabled:opacity-50"
          style={{ borderColor: `${accent}88`, color: accent, background: `${accent}10` }}
          data-testid={`${testid}-btn`}
          title="Upload photo (JPG/PNG · max 5MB)"
        >
          {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
          {previewSrc ? 'REPLACE' : 'UPLOAD DP'}
        </button>
        {previewSrc && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="inline-flex items-center px-1.5 py-1 rounded border border-[var(--eh-border)] hover:border-red-500 hover:text-red-400 text-[10px]"
            title="Remove photo — falls back to monogram"
            data-testid={`${testid}-clear`}
          >
            <X size={11} />
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      {!previewSrc && (
        <div className="eh-mono text-[9px] opacity-40 tracking-widest flex items-center gap-1">
          <ImageIcon size={9} /> no photo
        </div>
      )}
    </div>
  );
};

export default TeamContentManager;
